import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewModule as GridViewIcon,
  TableRows as TableViewIcon,
  ArticleOutlined as FullPageIcon,
  PictureInPictureAlt as PopupIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import RoleDetailContent from './RoleDetailContent';
import { rbacAccentAt, sortRolesForList } from '../../utils/rbacListUi';
import { formatModuleLabel } from '../../utils/moduleLabel';

const LAYOUT_KEY = 'erp4c.rolesListLayout';

const RolesList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [layout, setLayout] = useState(() => {
    try {
      const v = localStorage.getItem(LAYOUT_KEY);
      return v === 'table' ? 'table' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [viewDialogRoleId, setViewDialogRoleId] = useState(null);
  const [viewDialogTitle, setViewDialogTitle] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [granularOnly, setGranularOnly] = useState(false);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [rolesTotal, setRolesTotal] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    api
      .get('/roles/permissions')
      .then((r) => {
        const g = r.data?.data?.grouped || {};
        setModuleOptions(Object.keys(g).sort());
      })
      .catch(() => {});
  }, []);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (moduleFilter) params.module = moduleFilter;
      if (granularOnly) params.granular_only = 'true';
      const r = await api.get('/roles', { params });
      setRoles(r.data.data || []);
      setRolesTotal(r.data.pagination?.total ?? (r.data.data || []).length);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, moduleFilter, granularOnly]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSyncGranular = async () => {
    if (
      !window.confirm(
        'Create or update granular roles named like "Role - Accounts-View" for every standard module × action permission, each with only that one permission? Existing roles with those exact names will have permissions reset to that single permission. Special permissions (e.g. camera_watch) are not included.'
      )
    ) {
      return;
    }
    setSyncing(true);
    try {
      const r = await api.post('/roles/sync-granular');
      const d = r.data?.data || {};
      toast.success(
        `Granular roles synced: ${d.roles_created ?? 0} created, ${d.roles_description_updated ?? 0} descriptions updated (${d.matrix_permissions ?? 0} matrix permissions).`
      );
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const persistLayout = useCallback((mode) => {
    setLayout(mode);
    try {
      localStorage.setItem(LAYOUT_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const handleMenuOpen = (e, role) => {
    setAnchorEl(e.currentTarget);
    setSelectedRole(role);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const closeRoleDialog = useCallback(() => {
    setViewDialogRoleId(null);
    setViewDialogTitle('');
  }, []);

  const handleDelete = async () => {
    if (!window.confirm('Delete this role?')) {
      handleMenuClose();
      return;
    }
    try {
      await api.delete(`/roles/${selectedRole.id}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
    handleMenuClose();
  };

  const sortedRoles = useMemo(() => sortRolesForList(roles), [roles]);
  const roleSeqById = useMemo(
    () => new Map(sortedRoles.map((role, idx) => [role.id, idx + 1])),
    [sortedRoles]
  );
  const hasFilters = Boolean(debouncedSearch || moduleFilter || granularOnly);

  const renderRoleCard = (role, accentIndex) => {
    const accent = rbacAccentAt(accentIndex);
    const rolePerms = [...(role.permissions || role.Permissions || [])].sort((a, b) =>
      (a.name || a.code || '').localeCompare(b.name || b.code || '', undefined, { sensitivity: 'base' })
    );
    return (
      <Card
        sx={{
          height: '100%',
          borderLeft: `5px solid ${accent.border}`,
          bgcolor: accent.bg,
          transition: 'box-shadow 0.2s ease',
          '&:hover': { boxShadow: 4 },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${String(accentIndex + 1).padStart(2, '0')}`} size="small" />
                {role.reference_code && <Chip label={role.reference_code} size="small" variant="outlined" />}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {role.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {role.description}
              </Typography>
            </Box>
            <IconButton onClick={(e) => handleMenuOpen(e, role)}>
              <MoreVertIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Permissions ({rolePerms.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {rolePerms.slice(0, 5).map((p) => (
              <Chip key={p.id} label={p.name || p.code} size="small" />
            ))}
            {rolePerms.length > 5 && (
              <Chip label={`+${rolePerms.length - 5}`} size="small" variant="outlined" />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            App Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage roles and permissions. Use <strong>Sync granular roles</strong> to create{' '}
            <strong>Role - Module-Action</strong> roles (one standard permission each), then assign them to groups.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Tooltip title="Card view">
            <IconButton
              color={layout === 'grid' ? 'primary' : 'default'}
              onClick={() => persistLayout('grid')}
              aria-label="Show roles as cards"
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Row view">
            <IconButton
              color={layout === 'table' ? 'primary' : 'default'}
              onClick={() => persistLayout('table')}
              aria-label="Show roles as rows"
            >
              <TableViewIcon />
            </IconButton>
          </Tooltip>
          {hasPermission('roles_add') && (
            <>
              <Tooltip title='Ensures one role per module × action permission: name like Role - Accounts-View with only accounts_view'>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                    onClick={handleSyncGranular}
                    disabled={syncing}
                  >
                    Sync granular roles
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/roles/new')}
                sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
              >
                Add Role
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
          <TextField
            placeholder="Search name, description, reference…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="roles-module-filter-label">Module</InputLabel>
            <Select
              labelId="roles-module-filter-label"
              label="Module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>All modules</em>
              </MenuItem>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {formatModuleLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={granularOnly} onChange={(e) => setGranularOnly(e.target.checked)} />}
            label="Single permission only"
          />
        </Stack>
        {rolesTotal > 500 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {rolesTotal} roles match; list is capped at 500. Narrow search or pick a module.
          </Typography>
        )}
      </Card>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : layout === 'grid' ? (
        <Grid container spacing={3}>
          {sortedRoles.map((role, idx) => (
            <Grid item xs={12} sm={6} md={4} key={role.id}>
              {renderRoleCard(role, idx)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Seq</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Permissions</TableCell>
                  <TableCell align="right" padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRoles.map((role, rowIdx) => {
                  const accent = rbacAccentAt(rowIdx);
                  const rolePerms = [...(role.permissions || role.Permissions || [])].sort((a, b) =>
                    (a.name || a.code || '').localeCompare(b.name || b.code || '', undefined, { sensitivity: 'base' })
                  );
                  return (
                    <TableRow
                      key={role.id}
                      hover
                      sx={{ borderLeft: `4px solid ${accent.border}`, '& > *': { bgcolor: 'transparent' } }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{String(rowIdx + 1).padStart(2, '0')}</TableCell>
                      <TableCell>
                        {role.reference_code ? (
                          <Chip label={role.reference_code} size="small" variant="outlined" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" color="text.secondary" noWrap title={role.description}>
                          {role.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{rolePerms.length}</TableCell>
                      <TableCell align="right" padding="checkbox">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, role)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedRoles.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {hasFilters ? 'No roles match these filters.' : 'No roles yet.'}
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {!loading && layout === 'grid' && sortedRoles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {hasFilters ? 'No roles match these filters.' : 'No roles yet.'}
        </Typography>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/admin/roles/${selectedRole?.id}`);
            handleMenuClose();
          }}
        >
          <FullPageIcon sx={{ mr: 1, fontSize: 20 }} /> Full page
        </MenuItem>
        <MenuItem
          onClick={() => {
            const seq = selectedRole?.id ? roleSeqById.get(selectedRole.id) : null;
            setViewDialogRoleId(selectedRole?.id ?? null);
            setViewDialogTitle(`${seq ? `#${String(seq).padStart(2, '0')} ` : ''}${selectedRole?.name || ''}`);
            handleMenuClose();
          }}
        >
          <PopupIcon sx={{ mr: 1, fontSize: 20 }} /> Popup window
        </MenuItem>
        {hasPermission('roles_modify') && (
          <MenuItem
            onClick={() => {
              navigate(`/admin/roles/${selectedRole?.id}/edit`);
              handleMenuClose();
            }}
          >
            <EditIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
          </MenuItem>
        )}
        {hasPermission('roles_delete') && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog fullWidth maxWidth="md" open={Boolean(viewDialogRoleId)} onClose={closeRoleDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {viewDialogTitle || 'Role'}
          </Typography>
          <IconButton aria-label="Close" onClick={closeRoleDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewDialogRoleId && (
            <RoleDetailContent roleId={viewDialogRoleId} variant="dialog" onRequestClose={closeRoleDialog} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RolesList;
